import { Badge, Box, Flex, HStack, Icon, Stack, Text } from "@chakra-ui/react";
import { juryOnlyAdvises } from "./juryRanking";
import {
  designContestPrizesOf,
  eventIsDesignContest,
  tenderAwardCriteriaOf,
} from "../../sourcing-events/buyer/wizard/utils/designContest";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiCheckCircle, FiFileText } from "react-icons/fi";
import {
  AwardLetterDto,
  AwardLetterStatusDto,
  AwardLetterTypeDto,
  EvalBidDto,
  SourcingEventDto,
  useGetAwardLettersQuery,
  useGetEvaluationQuery,
} from "../../../autogen/bff-api";
import { useFinalStageRound } from "../rounds/RoundSelectionContext";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useRunsPublicProcurements } from "../../../common/organization/useRunsPublicProcurements";
import { evalCriterionLabel, evalCriterionWeightPercent } from "../evaluation/criterionLabel";
import { AwardLetterEditor } from "./AwardLetterEditor";
import { winnerNamesText } from "./awardLetterTemplates";
import {
  AwardLetterDraftBidSummary,
  AwardLetterDraftCriterion,
  AwardLetterDraftInput,
} from "./useAwardLetterDraft";

export const AwardLetterWorkbench = ({ event }: { event: SourcingEventDto }) => {
  const { t } = useTranslation();
  const finalStageRound = useFinalStageRound();
  const authState = useLoggedInWithOrgContextState();
  const runsPublicProcurements = useRunsPublicProcurements();
  const { data: lettersData } = useGetAwardLettersQuery({ eventId: event.id });
  const {
    data: evaluation,
    isLoading: criteriaLoading,
    isError: criteriaFailed,
    isFetching: criteriaRefetching,
    refetch: refetchCriteria,
  } = useGetEvaluationQuery({ eventId: event.id, round: finalStageRound?.id });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const letters = useMemo(() => {
    const all = lettersData?.letters ?? [];
    return [...all].sort((a, b) => (a.type === AwardLetterTypeDto.Winner ? -1 : b.type === AwardLetterTypeDto.Winner ? 1 : 0));
  }, [lettersData]);

  // Criterion descriptions live on the event's award criteria; merge them onto the evaluation's
  // (non-price) criteria, which already carry the resolved weightPercent.
  const criteria: AwardLetterDraftCriterion[] = useMemo(() => {
    const descriptions = new Map<string, string>();
    const eventCriteria = tenderAwardCriteriaOf(event)?.bestPriceQualityRatio?.criteria ?? [];
    for (const c of eventCriteria) {
      if (c.id && c.description) descriptions.set(c.id, c.description);
      // Sub-criteria are scorable rows too, so the letter can be asked to justify one of them.
      for (const sub of c.subCriteria ?? []) {
        if (sub.id && sub.description) descriptions.set(sub.id, sub.description);
      }
    }
    return (evaluation?.criteria ?? [])
      .filter((c) => !c.isPrice)
      .map((c) => ({
        id: c.id,
        name: evalCriterionLabel(c, t("Criterion") || ""),
        // The share of the WHOLE procurement — quoting a sub-criterion's share of its parent would
        // overstate in the letter what that criterion actually counted for.
        weightPercent: evalCriterionWeightPercent(c) ?? 0,
        description: descriptions.get(c.id) ?? null,
      }));
  }, [evaluation, event, t]);

  const winnerLetter = letters.find((l) => l.type === AwardLetterTypeDto.Winner);
  const winnerNames = letters
    .filter((l) => l.type === AwardLetterTypeDto.Winner)
    .map((l) => l.supplierName)
    .filter((name): name is string => !!name);
  const namesTheWinner = lettersData?.namesWinnerToOtherBidders ?? false;
  const winnerName = namesTheWinner ? winnerNamesText(winnerNames) || t("the winner") || "" : "";
  const winnerBid = useMemo(
    () => (winnerLetter ? evaluation?.bids.find((b) => b.bidId === winnerLetter.bidId) : undefined),
    [evaluation, winnerLetter],
  );

  const buildBidSummary = (
    supplierName: string,
    bid: EvalBidDto | undefined,
  ): AwardLetterDraftBidSummary => ({
    supplierName,
    responses: criteria.map((c) => {
      const cell = bid?.cells.find((cc) => cc.criterionId === c.id);
      return {
        criterionId: c.id,
        points: cell?.points ?? null,
        weightedPoints: cell?.weightedPoints ?? null,
        evaluatorComment: cell?.comment ?? null,
      };
    }),
    price:
      bid?.price && typeof bid.price.value === "number"
        ? { value: bid.price.value, currency: bid.price.currency }
        : null,
    rank: bid?.rank ?? null,
    weightedTotal: bid?.weightedTotal ?? null,
  });

  const aiInputFor = (letter: AwardLetterDto): AwardLetterDraftInput => {
    const pc = event.eventContext.type === "Public" ? event.eventContext : null;
    const recipientBid = evaluation?.bids.find((b) => b.bidId === letter.bidId);
    const isWinner = letter.type === AwardLetterTypeDto.Winner;
    const recipient = buildBidSummary(letter.supplierName || t("Unknown") || "", recipientBid);
    const winner = isWinner ? recipient : buildBidSummary(winnerName, winnerBid);
    return {
      letterType: isWinner ? "WINNER" : "REJECTION",
      eventTitle: event.eventData.title,
      eventValue:
        pc?.estimatedValue && typeof pc.estimatedValue.value === "number"
          ? { value: pc.estimatedValue.value, currency: pc.estimatedValue.currency }
          : null,
      criteria,
      recipient,
      winner,
    };
  };

  if (letters.length === 0) return null;

  const approvedCount = letters.filter(
    (l) => l.status === AwardLetterStatusDto.Approved || l.status === AwardLetterStatusDto.Sent,
  ).length;
  const allApproved = letters.every((l) => l.status === AwardLetterStatusDto.Approved);
  const isDispatched = letters.some((l) => l.status === AwardLetterStatusDto.Sent);
  const selected = letters.find((l) => l.id === selectedId);
  const designContest = eventIsDesignContest(event);

  return (
    <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p="5">
      <Flex justify="space-between" align="center" mb="3" wrap="wrap" gap="2">
        <HStack spacing="2">
          <FiFileText />
          <Text fontWeight="semibold" color="smText">
            {designContest ? t("Letters to the participants") : t("Award letters")}
          </Text>
        </HStack>
        <Text fontSize="sm" color="smMuted">
          {t("{{approved}} of {{total}} approved", { approved: approvedCount, total: letters.length })}
        </Text>
      </Flex>

      <Stack spacing="2" mb="4">
        {letters.map((letter) => {
          const done = letter.status === AwardLetterStatusDto.Approved || letter.status === AwardLetterStatusDto.Sent;
          return (
            <Flex
              key={letter.id}
              as="button"
              onClick={() => setSelectedId(letter.id)}
              justify="space-between"
              align="center"
              bg="smBackgroundSecondary"
              rounded="lg"
              border="1px solid"
              borderColor="smBorder"
              px="4"
              py="3"
              textAlign="left"
              _hover={{ borderColor: "smPrimary" }}
            >
              <HStack spacing="3">
                <Icon as={done ? FiCheckCircle : FiFileText} color={done ? "smSuccess" : "smMuted"} />
                <Box>
                  <Text fontWeight="medium" color="smText">
                    {letter.supplierName || t("Unknown")}
                  </Text>
                  <Text fontSize="xs" color="smMuted">
                    {letter.type === AwardLetterTypeDto.Winner
                      ? designContest
                        ? t("Letter to the winner")
                        : t("Winner letter")
                      : t("Rejection letter")}
                  </Text>
                </Box>
              </HStack>
              <Badge colorScheme={statusColor(letter.status)}>{statusLabel(letter.status, t)}</Badge>
            </Flex>
          );
        })}
      </Stack>

      {isDispatched ? (
        <Flex
          align="center"
          gap="2"
          bg="smSuccessSoft"
          color="smSuccess"
          border="1px solid"
          borderColor="smSuccessSoftBorder"
          rounded="md"
          px="4"
          py="3"
        >
          <FiCheckCircle />
          <Text fontSize="sm">
            {designContest
              ? t("The letters have been sent to the participants.")
              : t("Award letters have been sent to the suppliers.")}
          </Text>
        </Flex>
      ) : (
        <Text fontSize="xs" color="smMuted">
          {allApproved
            ? t("Every letter is approved. The Now panel at the top of the page sends them.")
            : t("All letters must be approved before they can be sent.")}
        </Text>
      )}

      {selected && (
        <AwardLetterEditor
          key={selected.id}
          eventId={event.id}
          letter={selected}
          evaluation={lettersData?.evaluations?.[selected.bidId]}
          aiInput={aiInputFor(selected)}
          criteriaLoad={{
            isLoading: criteriaLoading,
            isError: criteriaFailed,
            isRetrying: criteriaRefetching,
            retry: refetchCriteria,
          }}
          winnerName={winnerName}
          namesWinnerToOtherBidders={namesTheWinner}
          eventTitle={event.eventData.title}
          senderName={authState.selectedOrg.name}
          signerName={[event.responsiblePerson?.firstName, event.responsiblePerson?.lastName].filter(Boolean).join(" ")}
          clauseContext={{
            eventRunsPrivateFlow: event.eventContext.type === "Private",
            outsideProcurementAct: event.outsideProcurementAct ?? false,
            buyerRunsPublicProcurements: runsPublicProcurements,
            regulation: event.eventContext.type === "Public" ? event.eventContext.regulation ?? null : null,
            designContest,
            juryOnlyAdvises: juryOnlyAdvises(event),
            designContestPrizes: designContestPrizesOf(event),
            standstill: {
              buyerSetsStandstill: event.buyerSetsStandstill ?? false,
              voluntary: event.choosesVoluntaryStandstill ?? false,
              days: event.award?.standstillDays,
              exemption: event.award?.standstillExemption,
              expiresAt: event.award?.standstillExpiresAt,
              awaitsDispatch: !event.award?.standstillExpiresAt,
            },
          }}
          isOpen={!!selected}
          onClose={() => setSelectedId(null)}
        />
      )}
    </Box>
  );
};

const statusColor = (status: AwardLetterStatusDto): string => {
  switch (status) {
    case AwardLetterStatusDto.Sent:
      return "green";
    case AwardLetterStatusDto.Approved:
      return "teal";
    default:
      return "gray";
  }
};

const statusLabel = (status: AwardLetterStatusDto, t: (k: string) => string): string => {
  switch (status) {
    case AwardLetterStatusDto.Sent:
      return t("Sent");
    case AwardLetterStatusDto.Approved:
      return t("Approved");
    default:
      return t("Draft");
  }
};
